import { Delta } from "@vueup/vue-quill";

const transientAttributes = new Set([
  "data-teacher-comment",
  "data-highlight",
  "data-highlight-block",
  "data-banned-word",
  "data-selection-blot",
  "data-is-selected",
  "data-selection-start",
  "data-selection-end",
]);

// Align saved identifications with the graded text before adding teacher feedback.
// Diffing plain text preserves attributes on retained text without overwriting
// the teacher's formatting or marking inserted feedback as student selections.
const mapGuidedReviewAttributes = (sourceOps: unknown, text: string) => {
  if (!Array.isArray(sourceOps)) return undefined;
  const source = new Delta();
  const plainSource = new Delta();
  for (const op of sourceOps) {
    if (op?.insert == null) continue;
    const attributes = Object.fromEntries(Object.entries(op.attributes ?? {}).filter(
      ([key, value]) => key.startsWith("data-") && !transientAttributes.has(key) && value != null
    ));
    source.insert(op.insert, attributes);
    plainSource.insert(op.insert);
  }
  return source.compose(plainSource.diff(new Delta().insert(text)));
};

export const useConvertParagraphsToDelta = (
  paragraphs: Paragraph[],
  annotations?: RevisionAnnotations,
  revisionCommentObject?: any,
  teacherCommentColor?: number[],
  includeTeacherDeletions = false,
  sourceStudentSubmissionOps?: unknown
) => {
  const appUserDataStore = useAppUserDataStore();
  const { commentColor } = storeToRefs(appUserDataStore);

  const quillDelta = new Delta();
  const paragraphIndent = (paragraph: Paragraph) =>
    "\t".repeat(Math.max(0, useMapFirstLineIndentToWidth(paragraph.formatting.firstLineIndent) ?? 0));
  const studentText = paragraphs.map(paragraph =>
    paragraphIndent(paragraph) +
    (paragraph.tokens ?? []).map(token => token.text.replace(/\n/g, "")).join("") + "\n"
  ).join("");
  const guidedReviewDelta = mapGuidedReviewAttributes(sourceStudentSubmissionOps, studentText);
  let studentTextPosition = 0;
  const insertStudentText = (text: string, attributes: Record<string, any>) => {
    if (guidedReviewDelta) {
      for (const op of guidedReviewDelta.slice(studentTextPosition, studentTextPosition + text.length).ops) {
        quillDelta.insert(op.insert!, { ...op.attributes, ...attributes });
      }
    } else {
      quillDelta.insert(text, attributes);
    }
    studentTextPosition += text.length;
  };
  const commentColorHex = useRGBToHex(
    teacherCommentColor ?? commentColor.value
  );
  const teacherCommentAttributes = (attributes: Record<string, any> = {}) => ({
    ...attributes,
    color: commentColorHex,
    [TEACHER_COMMENT_ATTRIBUTE]: "true",
  });

  const allAnnotations = annotations ?? [];

  let paragraphIndex = 0;
  for (const paragraph of paragraphs) {
    if (paragraphIndex == 0) {
      const startDocumentAnnotations = useParagraphAnnotations(
        allAnnotations,
        paragraph,
        paragraph.tokens,
        true
      );

      for (const annotation of startDocumentAnnotations) {
        const annotationText = useConvertQuillToPlaintext(
          annotation.commentObject
        );
        quillDelta.insert(` ${annotationText} `, {
          ...teacherCommentAttributes(),
        });
        quillDelta.insert(`\n`, {
          color: commentColorHex,
          lineheight: "2",
        });
      }
    }

    const tokens = paragraph.tokens ?? [];

    insertStudentText(paragraphIndent(paragraph), {});

    for (const token of tokens) {
      const startLineAnnotations = useStartLineAnnotations(
        allAnnotations,
        paragraph,
        token
      );

      if (startLineAnnotations) {
        for (const annotation of startLineAnnotations) {
          const annotationText = useConvertQuillToPlaintext(
            annotation.commentObject
          );
          quillDelta.insert(`${annotationText} `, {
            ...teacherCommentAttributes(),
          });
        }
      }

      // Strip all line breaks from the token text
      const text = token.text.replace(/\n/g, "");
      let color: string | undefined;

      if (token.formatting.color) {
        color = useRGBToHex(token.formatting.color);
      }

      let backgroundColor: string | undefined;

      if (token.formatting.highlightColor) {
        // If it's an array useRGBToHex
        if (Array.isArray(token.formatting.highlightColor)) {
          backgroundColor = useRGBToHex(token.formatting.highlightColor);
        } else {
          backgroundColor = token.formatting.highlightColor;
        }
      }

      insertStudentText(text, {
        bold: token.formatting.bold,
        underline: token.formatting.underline,
        italic: token.formatting.italic,
        strike: token.formatting.strikeThrough,
        color: color,
        background: backgroundColor,
        ...(includeTeacherDeletions && token.formatting.strikeThrough
          ? {
              ...teacherCommentAttributes(),
              [TEACHER_COMMENT_ATTRIBUTE]: TEACHER_DELETION_VALUE,
            }
          : {}),
      });

      const tokenAnnotations = useParagraphTokenAnnotations(
        allAnnotations,
        paragraph,
        token
      );

      if (tokenAnnotations) {
        for (const annotation of tokenAnnotations) {
          const isLong = useIsAnnotationCommentLong(annotation);
          const text = isLong
            ? "See below."
            : useConvertQuillToPlaintext(annotation.commentObject);

          quillDelta.insert(` ${text} `, {
            ...teacherCommentAttributes(),
          });
        }
      }
    }

    // Map your model to Quill's block attrs
    const blockAttrs: Record<string, any> = {};

    switch (paragraph.formatting.alignment) {
      case "CENTER":
        blockAttrs.align = "center";
        break;
      case "RIGHT":
        blockAttrs.align = "right";
        break;
      case "JUSTIFY":
        blockAttrs.align = "justify";
        break;
      // LEFT or undefined -> omit (left is default)
    }

    // Indentation -> 'indent' (integer levels)
    if (
      paragraph.formatting.indentation &&
      paragraph.formatting.indentation > 0
    ) {
      blockAttrs.indent = paragraph.formatting.indentation; // e.g., 1, 2, ...
    }

    // Line spacing -> custom 'lineheight' format (string values like "1.5", "2")
    if (paragraph.formatting.lineSpacing) {
      blockAttrs.lineheight = String(
        Math.floor(paragraph.formatting.lineSpacing)
      );
    }

    // Close the paragraph with attributes on the newline
    insertStudentText("\n", blockAttrs);

    const paragraphLongTokenAnnotations = useLongTokenAnnotations(
      allAnnotations,
      paragraph,
      paragraph.tokens
    );

    if (paragraphLongTokenAnnotations.length > 0) {
      quillDelta.insert(`\n`, {
        color: commentColorHex,
      });
    }

    for (const annotation of paragraphLongTokenAnnotations) {
      const matchingTokens = paragraph.tokens.filter((token) =>
        annotation.tokenGlobalPositions.includes(token.globalPosition)
      );

      const tokenText = matchingTokens.map((token) => token.text).join("");

      quillDelta.insert(`${tokenText} - `, {
        ...teacherCommentAttributes({ bold: true }),
      });

      const annotationText = useConvertQuillToPlaintext(
        annotation.commentObject
      );
      quillDelta.insert(`${annotationText} `, {
        ...teacherCommentAttributes(),
      });
      quillDelta.insert(`\n`, {
        color: commentColorHex,
        lineheight: "2",
      });
    }

    const paragraphAnnotations = useParagraphAnnotations(
      allAnnotations,
      paragraph,
      paragraph.tokens,
      false
    );

    if (
      paragraphAnnotations.length > 0 &&
      paragraphLongTokenAnnotations.length == 0
    ) {
      // quillDelta.insert(`\n`, {
      //   color: useRGBToHex(commentColor.value),
      // });
    }

    for (const annotation of paragraphAnnotations) {
      for (const op of annotation.commentObject) {
        quillDelta.insert(op.insert, {
          ...teacherCommentAttributes(op.attributes),
        });
      }

      quillDelta.insert(`\n`, {
        lineheight: "2",
        color: commentColorHex,
      });
    }

    paragraphIndex++;
  }

  // Add the revisoin commetn which is an ops array to the delta
  if (revisionCommentObject) {
    quillDelta.insert(`\n`, {
      lineheight: "2",
      color: commentColorHex,
    });

    for (const op of revisionCommentObject) {
      quillDelta.insert(op.insert, {
        ...teacherCommentAttributes(op.attributes),
      });
    }
  }

  return quillDelta;
};
